import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Loader2, Trash2 } from "lucide-react";
import { useConfirm } from "../../components/ConfirmProvider";

/**
 * Manage the master feature-flag registry. Tiers reference these by `key`;
 * this dialog is where super_admin creates new toggles that tier editors can
 * then flip on/off per tier.
 *
 * Deleting a feature cascade-removes it from every tier's features list AND
 * every firm's stored snapshot, so no stale references remain.
 */
export default function FeatureRegistryDialog({ features, onClose, onSaved }) {
  const [key, setKey] = useState("");
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("general");
  const [saving, setSaving] = useState(false);
  const [deletingKey, setDeletingKey] = useState(null);
  const confirm = useConfirm();

  const submit = async (e) => {
    e.preventDefault();
    if (!key.trim() || !label.trim()) return toast.error("Key and label are required");
    setSaving(true);
    try {
      await api.post("/superadmin/subscription-features", {
        key: key.trim().toLowerCase(),
        label: label.trim(),
        description: description.trim(),
        category,
      });
      toast.success("Feature added");
      setKey(""); setLabel(""); setDescription(""); setCategory("general");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (f) => {
    const ok = await confirm({
      title: `Delete feature "${f.label}"?`,
      message: "It will be removed from every tier and every firm's stored snapshot. This cannot be undone.",
      confirmText: "Delete",
      danger: true,
    });
    if (!ok) return;
    setDeletingKey(f.key);
    try {
      await api.delete(`/superadmin/subscription-features/${encodeURIComponent(f.key)}`);
      toast.success("Feature deleted");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeletingKey(null);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-w-xl max-h-[90vh] overflow-y-auto"
        data-testid="feature-registry-dialog"
      >
        <DialogHeader>
          <DialogTitle>Manage features</DialogTitle>
          <DialogDescription>
            Feature flags any subscription tier can enable. Backend code gates on the machine <code className="text-[11px] bg-[#F7F4EC] px-1.5 py-0.5 rounded">key</code>.
          </DialogDescription>
        </DialogHeader>

        {/* Existing features */}
        <section className="mt-2">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] mb-2">
            Existing features ({features.length})
          </div>
          {features.length === 0 ? (
            <p className="text-sm text-[#7A7571] italic">No features registered yet.</p>
          ) : (
            <ul className="space-y-2">
              {features.map((f) => (
                <li key={f.key} className="p-3 rounded-2xl bg-[#F7F4EC]" data-testid={`feature-row-${f.key}`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="font-semibold text-[#2C2A29]">{f.label}</div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <code className="text-[11px] text-[#7A7571] font-mono">{f.key}</code>
                      <button
                        type="button"
                        onClick={() => remove(f)}
                        disabled={deletingKey === f.key}
                        data-testid={`feature-delete-${f.key}`}
                        className="inline-flex items-center gap-1 text-xs text-[#7A7571] hover:text-[#9F1239] disabled:opacity-50"
                        title="Delete feature (cascade-removes from all tiers)"
                      >
                        {deletingKey === f.key
                          ? <Loader2 size={12} className="animate-spin" />
                          : <Trash2 size={12} />}
                        Delete
                      </button>
                    </div>
                  </div>
                  {f.description && <div className="text-xs text-[#7A7571] mt-1">{f.description}</div>}
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Add form */}
        <form onSubmit={submit} className="mt-6 pt-6 border-t border-[#EBE5D9] space-y-3">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571]">Add feature</div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs font-semibold text-[#4B4642] mb-1">Machine key</div>
              <input
                type="text" required
                value={key}
                onChange={(e) => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))}
                data-testid="new-feature-key"
                className="input-soft font-mono"
                placeholder="ai_knowledge_base"
              />
              <div className="text-[11px] text-[#7A7571] mt-1">snake_case, lowercase, alphanumeric</div>
            </label>
            <label className="block">
              <div className="text-xs font-semibold text-[#4B4642] mb-1">Display label</div>
              <input
                type="text" required
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                data-testid="new-feature-label"
                className="input-soft"
                placeholder="AI Knowledge Base"
              />
            </label>
          </div>
          <label className="block">
            <div className="text-xs font-semibold text-[#4B4642] mb-1">Description</div>
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="new-feature-description"
              className="input-soft"
              placeholder="What tier subscribers get when this is enabled."
            />
          </label>
          <label className="block">
            <div className="text-xs font-semibold text-[#4B4642] mb-1">Category</div>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="new-feature-category"
              className="input-soft w-48"
            >
              <option value="general">General</option>
              <option value="support">Support</option>
              <option value="collaboration">Collaboration</option>
              <option value="reporting">Reporting</option>
              <option value="integration">Integration</option>
            </select>
          </label>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost" data-testid="feature-dialog-close">Close</button>
            <button
              type="submit"
              disabled={saving}
              data-testid="add-feature-btn"
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Add feature
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
